package ai.wagemix.web.components

import kotlin.math.abs
import kotlin.math.roundToInt

/*
 * Presentation formatting shared by every component and presentation model. Presenters never
 * format; they emit numbers and these functions turn them into the strings the boards show.
 */
object WMFormat {

    /** `$51,800` */
    fun money(value: Int): String = "$" + groupThousands(value)

    /** `$51.8K`, `$128.4K`, `$1.2M`; whole dollars below one thousand. */
    fun moneyShort(value: Int): String {
        val magnitude = abs(value)
        return when {
            magnitude >= 1_000_000 -> "$" + oneDecimal(value / 1_000_000.0) + "M"
            magnitude >= 1_000 -> "$" + oneDecimal(value / 1_000.0) + "K"
            else -> money(value)
        }
    }

    /** `$37K`, `$129K`: the whole-thousand form the stat tiles and chart axis use. */
    fun moneyThousands(value: Int): String {
        val magnitude = abs(value)
        return when {
            magnitude >= 1_000_000 -> "$" + oneDecimal(value / 1_000_000.0) + "M"
            magnitude >= 1_000 -> "$" + (value / 1_000.0).roundToInt() + "K"
            else -> money(value)
        }
    }

    /** `$24.90` for an hourly wage. */
    fun hourly(value: Float): String = "$" + twoDecimals(value.toDouble())

    /** `$18` for a rounded hourly wage in compact ranges. */
    fun hourlyWhole(value: Float): String = "$" + value.roundToInt()

    /** `$37K – $81K` (annual) or `$18.42 – $39.07` (hourly: BLS publishes hourly wages to the cent, so keep them). */
    fun range(low: Int, high: Int): String = moneyThousands(low) + " – " + moneyThousands(high)
    fun hourlyRange(low: Float, high: Float): String = hourly(low) + " – " + hourly(high)

    /** `+3.2%` / `−1.4%` */
    fun percentDelta(fraction: Double): String {
        val percent = fraction * 100
        val sign = if (percent >= 0) "+" else "−"
        return sign + oneDecimal(abs(percent)) + "%"
    }

    /** `60%` */
    fun percent(value: Double): String = value.roundToInt().toString() + "%"

    /** `88 KB`, `1.2 MB` */
    fun fileSize(bytes: Long): String = when {
        bytes >= 1_048_576 -> oneDecimal(bytes / 1_048_576.0) + " MB"
        bytes >= 1024 -> (bytes / 1024.0).roundToInt().toString() + " KB"
        else -> "$bytes B"
    }

    fun initials(firstName: String?, lastName: String?, fallback: String): String {
        val first = firstName?.trim()?.firstOrNull()
        val last = lastName?.trim()?.firstOrNull()
        return when {
            first != null && last != null -> "$first$last".uppercase()
            first != null -> first.uppercase().toString()
            else -> fallback.trim().take(2).uppercase()
        }
    }

    private fun groupThousands(value: Int): String {
        val digits = abs(value).toString()
        val grouped = digits.reversed().chunked(3).joinToString(",").reversed()
        return if (value < 0) "−$grouped" else grouped
    }

    private fun oneDecimal(value: Double): String {
        val text = value.asDynamic().toFixed(1) as String
        return text.removeSuffix(".0")
    }

    private fun twoDecimals(value: Double): String = value.asDynamic().toFixed(2) as String
}
